import React, { useContext, useId, useState } from 'react';
import { Box, Collapse, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AppContext } from '../../context/app-context';
import { SubscriptionType } from '../../components/constants/securspace-constants';
import MobileAppPromoHeader from './MobileAppPromoHeader';
import MobileAppStoreBadges from './MobileAppStoreBadges';
import MobileAppFeatureList from './MobileAppFeatureList';

export interface MobileAppPromoProps {
  /** Optional heading copy override. */
  title?: string;
  /** Optional supporting copy override. */
  description?: string;
  /** Whether the body starts expanded. Defaults to false (collapsed). */
  defaultExpanded?: boolean;
}

const MobileAppPromoRoot = styled(Box)({
  width: '100%',
  flexBasis: '100%',
  backgroundColor: '#fff',
  border: '1px solid #e0e6ec',
  borderRadius: '8px',
  marginBottom: '24px',
  overflow: 'hidden',
  boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
});

const MobileAppPromoBody = styled(Box)({
  padding: '16px 20px',
});

/**
 * Collapsible banner encouraging GMS PRO gate users to download the SecurSpace
 * Gate Check mobile app to check equipment in and out from their phone.
 *
 * Mirrors the "Getting Started With Locations" InfoBanner: a white rounded card
 * with a gradient header row and rounded app-icon. The header acts as a Show/Hide
 * toggle, so the banner can be collapsed and re-opened at any time (it is never
 * dismissed permanently). Store links are custom App Store / Google Play links
 * (colored icon + text), not the official full-color store badges.
 *
 * Only rendered for GMS PRO subscribers; returns null for any other subscription
 * type (e.g. GMS Lite), since the Gate Check app is a GMS PRO feature.
 */
const MobileAppPromo = ({
  title = 'Check equipment in & out from your phone or tablet',
  description = 'Download the SecurSpace Gate Check Mobile App.',
  defaultExpanded = false,
}: MobileAppPromoProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { user } = useContext(AppContext);
  const bodyId = useId();

  if (user?.subscriptionType !== SubscriptionType.GMS_PRO) {
    return null;
  }

  return (
    <MobileAppPromoRoot>
      <MobileAppPromoHeader
        title={title}
        description={description}
        isExpanded={isExpanded}
        bodyId={bodyId}
        onClick={() => setIsExpanded((prev) => !prev)}
      />
      <Collapse in={isExpanded}>
        <MobileAppPromoBody
          id={bodyId}
          role="region"
          aria-label="Download the SecurSpace mobile app"
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2.5}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <MobileAppFeatureList />
            <MobileAppStoreBadges />
          </Stack>
        </MobileAppPromoBody>
      </Collapse>
    </MobileAppPromoRoot>
  );
};

export default MobileAppPromo;
